import { cx } from '../../utils/cx';
import formatNotificationCount from '../../utils/formatNotificationCount';

interface ICountPipProps {
  /** Number to display. Renders nothing at zero or below. */
  count: number;
  /** Highest number shown verbatim; anything above renders as `"<max>+"`. Defaults to 9. */
  max?: number;
  /** Position / sizing overrides. Merged with the default `absolute -right-1 -top-0.5 size-4`. */
  className?: string;
}

/**
 * Small brand-solid count pip designed to overlay an icon — the unread
 * counter on a notification bell being the canonical use. Renders `null`
 * when `count` is zero or below, so callers can mount it unconditionally.
 *
 * Render the pip as a sibling of the icon inside a `relative`-positioned
 * wrapper:
 *
 *     <span className="relative inline-flex">
 *       <BellIcon className={...} />
 *       <CountPip count={unreadCount} />
 *     </span>
 *
 * `pointer-events-none` is load-bearing: the pip overlaps the corner of the
 * interactive element it decorates, and without it that corner would swallow
 * clicks and suppress the host's hover state.
 *
 * `aria-hidden` keeps the clamped display value ("9+") out of the assistive-tech
 * tree — the surrounding control must carry the true count in its accessible
 * name.
 */
const CountPip = ({ count, max, className }: ICountPipProps) => {
  const label = formatNotificationCount(count, max);
  if (label === null) {
    return null;
  }

  return (
    <span
      aria-hidden="true"
      className={cx(
        'bg-brand-solid text-utility-brand-25 pointer-events-none absolute -right-1 -top-0.5 flex size-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-medium leading-none',
        className
      )}
    >
      {label}
    </span>
  );
};

export default CountPip;
